import { createElement } from 'react';
import type { GridCellTemplateContext, GridCellTemplateRenderResult } from './types';

type ConditionalColorParams = {
  lookupProperty?: string;
  propertyValue?: unknown;
  color?: string;
  operator?: string;
};

const COMPARATORS: Record<string, (a: unknown, b: unknown) => boolean> = {
  '<': (a, b) => (a as number) < (b as number),
  '<=': (a, b) => (a as number) <= (b as number),
  '>': (a, b) => (a as number) > (b as number),
  '>=': (a, b) => (a as number) >= (b as number),
  '===': (a, b) => a === b,
  '!==': (a, b) => a !== b,
  '==': (a, b) => String(a) === String(b),
  '!=': (a, b) => String(a) !== String(b),
};

const compareValues = (actual: unknown, expected: unknown, operator: string): boolean => {
  const comparator = COMPARATORS[operator];
  if (!comparator) {
    console.warn(`formatCellColorConditionally: unsupported operator "${operator}"`);
    return false;
  }
  return comparator(actual, expected);
};

export const formatCellColorConditionally = ({
  value,
  displayValue = String(value ?? ''),
  rawLookupItems,
  rawParams,
}: GridCellTemplateContext): GridCellTemplateRenderResult | null => {
  if (!rawParams || !rawLookupItems?.length) return null;

  let params: ConditionalColorParams;
  try {
    params = JSON.parse(rawParams) as ConditionalColorParams;
  } catch {
    return null;
  }

  if (!params.lookupProperty || !params.operator) return null;

  const strValue = String(value ?? '');
  const lookupItem = rawLookupItems.find((item) => String(item.Id) === strValue) as
    Record<string, unknown> | undefined;
  if (!lookupItem) return null;

  let actualValue: unknown;
  if (Object.prototype.hasOwnProperty.call(lookupItem, params.lookupProperty)) {
    actualValue = lookupItem[params.lookupProperty];
  } else {
    const fields = lookupItem.Fields as
      Array<{ Name: string; StringValue: string | null }> | undefined;
    const field = fields?.find((f) => f.Name === params.lookupProperty);
    if (!field) return null;
    const sv = field.StringValue;
    if (sv === 'true') actualValue = true;
    else if (sv === 'false') actualValue = false;
    else if (sv !== null && sv !== '' && !isNaN(Number(sv))) actualValue = Number(sv);
    else actualValue = sv;
  }

  if (!compareValues(actualValue, params.propertyValue, params.operator)) return null;
  if (params.color === 'transparent') return { content: null };

  return {
    content: createElement(
      'span',
      { style: params.color ? { color: params.color } : undefined },
      displayValue
    ),
  };
};
